import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MainLayout } from '@/components/layout/MainLayout';
import { PageHeader } from '@/components/layout/PageHeader';
import { usePrestador } from '@/hooks/usePrestadores';
import { useAuthContext } from '@/contexts/AuthContext';
import { ArrowLeft, Loader2 } from 'lucide-react';

/**
 * Página para visualizar o cadastro de um prestador (usado pelo admin na Equipe).
 * Mostra avatar/nome, estado, dados pessoais (email, telefone, endereço) e observações.
 */
export default function VisualizarPrestador() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isAdmin } = useAuthContext();

  const { data: prestador, isLoading: carregandoPrestador } = usePrestador(id);

  if (!isAdmin) {
    navigate('/equipe', { replace: true });
    return null;
  }

  if (carregandoPrestador) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </MainLayout>
    );
  }

  if (!prestador) {
    return (
      <MainLayout>
        <div className="p-6 text-center flex flex-col items-center gap-2">
          <p className="text-muted-foreground">{t('team.provider.notFound', 'Prestador não encontrado')}</p>
          <button
            type="button"
            className="ow-btn ow-btn--ghost ow-btn--sm"
            onClick={() => navigate('/equipe')}
          >
            <ArrowLeft className="h-4 w-4" />
            {t('team.backToTeam', 'Voltar para Equipe')}
          </button>
        </div>
      </MainLayout>
    );
  }

  const p = prestador.profile;
  const nome = p?.nome || t('common.noName');
  const inicial = nome.charAt(0).toUpperCase();
  const naoInformado = t('common.notInformed', 'Não informado');

  const campos: { label: string; valor?: string | null }[] = [
    { label: t('common.email'), valor: p?.email },
    { label: t('common.phone'), valor: p?.telefone },
    { label: t('personalDataForm.address'), valor: p?.morada },
    { label: t('personalDataForm.postalCode'), valor: p?.codigo_postal },
    { label: t('personalDataForm.city'), valor: p?.cidade },
    { label: t('personalDataForm.country'), valor: p?.pais },
  ];

  return (
    <MainLayout>
      <div className="p-6 max-w-3xl mx-auto flex flex-col gap-5">
        <PageHeader
          titulo={nome}
          acoes={
            <button
              type="button"
              className="ow-btn ow-btn--ghost ow-btn--sm"
              onClick={() => navigate('/equipe')}
            >
              <ArrowLeft size={14} />
              {t('team.backToTeam', 'Voltar')}
            </button>
          }
        />

        {/* Cabeçalho do prestador */}
        <div className="ow-card">
          <div className="p-6 flex items-center gap-4">
            <span
              className="cli-itm__avt-img"
              style={{ width: 64, height: 64, fontSize: 22 }}
            >
              {p?.avatar_url ? <img src={p.avatar_url} alt={nome} /> : inicial}
            </span>
            <div className="flex-1 min-w-0">
              <h2 className="text-xl font-bold tracking-tight">{nome}</h2>
            </div>
          </div>
        </div>

        {/* Dados pessoais */}
        <div className="ow-card">
          <div className="px-6 pt-5 pb-3">
            <h3 className="text-base font-bold tracking-tight">{t('personalDataForm.title')}</h3>
          </div>
          <div className="px-6 pb-5 grid gap-4 sm:grid-cols-2">
            {campos.map((c) => (
              <div key={c.label} className="space-y-1">
                <span className="text-xs text-muted-foreground">{c.label}</span>
                <p className="text-sm font-medium">{c.valor || naoInformado}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Observações */}
        {prestador.observacoes && (
          <div className="ow-card">
            <div className="px-6 pt-5 pb-3">
              <h3 className="text-base font-bold tracking-tight">
                {t('common.notes', 'Observações')}
              </h3>
            </div>
            <div className="px-6 pb-5">
              <p className="text-sm whitespace-pre-wrap text-foreground">
                {prestador.observacoes}
              </p>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
}
